import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { Toaster } from 'sonner';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import OnboardingPage from './pages/OnboardingPage';
import BriefPage from './pages/BriefPage';
import SalesPage from './pages/SalesPage';
import WeatherPage from './pages/WeatherPage';
import PaymentsPage from './pages/PaymentsPage';
import CostsPage from './pages/CostsPage';
import SettingsSection from './pages/SettingsSection';
import SharePage from './pages/SharePage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import AdminPage from './pages/AdminPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PitchPage from './pages/PitchPage';
import DemoPage from './pages/DemoPage';
import SampleAuditPage from './pages/SampleAuditPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import FeaturesPage from './pages/FeaturesPage';
import DashboardLayout from './components/DashboardLayout';
import LoadingScreen from './components/LoadingScreen';
import KeepQueryRedirect from './components/KeepQueryRedirect';
import { LEGACY_REDIRECTS } from './utils/navMap';

function RedirectIfAuthed({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  // Render children while auth resolves so anonymous visitors never wait on a
  // loading screen to see the landing page.
  if (!loading && user) {
    return <Navigate to={user.onboarding_completed ? '/home' : '/onboarding'} replace />;
  }
  return <>{children}</>;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) {
    return <LoadingScreen />;
  }
  if (!user) return <Navigate to="/auth" replace />;
  if (!user.onboarding_completed && window.location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            fontFamily: 'DM Sans, system-ui, sans-serif',
            borderRadius: '8px',
            border: '1px solid #E6E2DE',
          },
        }}
      />
      <Routes>
        <Route path="/" element={<RedirectIfAuthed><LandingPage /></RedirectIfAuthed>} />
        <Route path="/auth" element={<RedirectIfAuthed><AuthPage /></RedirectIfAuthed>} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <DashboardLayout><BriefPage /></DashboardLayout>
            </ProtectedRoute>
          }
        />
        {/* Five doors: Today (/home), Sales, Payments, Costs, Settings. */}
        <Route
          path="/sales/*"
          element={
            <ProtectedRoute>
              <DashboardLayout><SalesPage /></DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payments"
          element={
            <ProtectedRoute>
              <DashboardLayout><PaymentsPage /></DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/costs"
          element={
            <ProtectedRoute>
              <DashboardLayout><CostsPage /></DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/*"
          element={
            <ProtectedRoute>
              <DashboardLayout><SettingsSection /></DashboardLayout>
            </ProtectedRoute>
          }
        />
        {/* Old URLs (emails, bookmarks, Square/Stripe returns) redirect with their query kept. */}
        {Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
          <Route key={from} path={from} element={<KeepQueryRedirect to={to} />} />
        ))}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminPage />
            </ProtectedRoute>
          }
        />
        {/* Dev only, no sign-in: the weather page from a fixture, for screenshots. Dropped from a production build. */}
        {process.env.NODE_ENV !== 'production' && (
          <Route
            path="/dev/weather"
            element={<div className="min-h-screen bg-[#FAF9F7]"><div className="mx-auto max-w-7xl px-4 py-6 sm:px-6"><WeatherPage /></div></div>}
          />
        )}
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/pitch" element={<PitchPage />} />
        <Route path="/demo" element={<DemoPage />} />
        <Route path="/audit" element={<SampleAuditPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/share/:token" element={<SharePage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
